import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUser from 'src/hooks/use-user';
import { useMemoArray } from 'hooks/use-memo-array';

import { PersonalizedProductsModelNames } from 'types/graphql';

import {
  useProductRecommendations,
  GetRecommendations,
  GetRecommendationsFallback,
  Options,
} from './use-product-recommendations';

import { getDispensaryFilter } from './recommendations.utils';
import { Scenarios } from './recommendations.types';
import { usePersonalizedItemsV2 } from './use-personalized-items-v2';

/**
 * Maps Scenarios to PersonalizedProductsModelNames for the V2 implementation
 */
function getModelNameFromScenario(scenario: Scenarios): PersonalizedProductsModelNames {
  switch (scenario) {
    case Scenarios.cart1:
      return PersonalizedProductsModelNames.cartToppers;
    case Scenarios.productDetailsPage1:
      return PersonalizedProductsModelNames.relatedItems;
    case Scenarios.productDetailsPage2:
      return PersonalizedProductsModelNames.oftenBoughtWith;
    default:
      return PersonalizedProductsModelNames.recommendedForYou;
  }
}

export function usePersonalizedItems(
  getRecommendationsFallback: GetRecommendationsFallback,
  itemIds: string[],
  scenario: Scenarios,
  options: Options
): ReturnType<typeof useProductRecommendations> {
  const flags = useFlags();
  const usePersonalizedProductsV2Flag = flags['growth.platform.use-personalized-products-v2.rollout'];
  // Always call hooks in the same order
  const { limit, skip } = options;
  const { id: userId } = useUser();
  const itemIdsMemo = useMemoArray(itemIds);

  const getRecommendations = useCallback<GetRecommendations>(
    (client, dispensary) =>
      client.getPersonalizedItems(userId ?? `anonymous`, itemIdsMemo, {
        scenario,
        amt: limit,
        filters: [getDispensaryFilter(dispensary)],
      }),
    [userId, itemIdsMemo, scenario, limit]
  );

  const originalResult = useProductRecommendations(getRecommendations, getRecommendationsFallback, {
    ...options,
    excludeProducts: itemIds,
    skip: !!skip || usePersonalizedProductsV2Flag, // Skip original when V2 flag is enabled
  });

  const modelName = getModelNameFromScenario(scenario);
  const v2Result = usePersonalizedItemsV2(getRecommendationsFallback, itemIds, modelName, {
    ...options,
    skip: !!skip || !usePersonalizedProductsV2Flag,
  });

  // Return the appropriate result based on feature flag
  return usePersonalizedProductsV2Flag ? v2Result : originalResult;
}
